import { useEffect, useState } from "react";
import { Stethoscope, Loader2, CheckCircle2, XCircle, Bot, Zap, UserRound, Ban, BellRing, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TRIGGERS } from "@/pages/automationMeta";

const RESPONDER = {
  automation: { label: "Risponde l'automazione", icon: Zap, cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  session: { label: "Continua l'automazione in corso", icon: Zap, cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  ai: { label: "Risponde l'Assistente IA", icon: Bot, cls: "bg-violet-50 border-violet-200 text-violet-800" },
  operator: { label: "Nessuna risposta: chat in mano all'operatore", icon: UserRound, cls: "bg-amber-50 border-amber-200 text-amber-800" },
  stop: { label: "Disiscrizione", icon: Ban, cls: "bg-slate-100 border-slate-200 text-slate-700" },
  none: { label: "Nessuna risposta automatica", icon: Ban, cls: "bg-slate-100 border-slate-200 text-slate-700" },
};

export function DiagnoseDialog() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/automations/diagnose", { message, phone }); setRes(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Diagnosi non riuscita"); }
    finally { setBusy(false); }
  };
  const R = res ? (RESPONDER[res.responder] || RESPONDER.none) : null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="diagnose-message-button" className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg">
          <Stethoscope className="h-4 w-4 mr-2" /> Diagnosi messaggio
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg [&>*]:min-w-0" data-testid="diagnose-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Chi risponderebbe a questo messaggio?</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Incolla il messaggio del cliente: vedi quale automazione (o l'IA) risponderebbe e perché. Il numero è facoltativo: serve per considerare sessioni attive, campagne ricevute e primo contatto.</p>
        <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Es. Vorrei il volantino" data-testid="diagnose-message-input" className="bg-white border-slate-300 text-slate-900 resize-none" />
        <div className="flex gap-2">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numero cliente (facoltativo) +39…" data-testid="diagnose-phone-input" className="bg-white border-slate-300 text-slate-900 flex-1" />
          <Button onClick={run} disabled={busy || !message.trim()} data-testid="diagnose-run-button" className="bg-[#5BC65F] hover:bg-[#45B049] text-white font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analizza"}</Button>
        </div>
        {res && (
          <div className="space-y-3" data-testid="diagnose-result">
            <div className={`rounded-xl border px-3 py-2.5 flex items-start gap-2.5 ${R.cls}`} data-testid="diagnose-responder">
              <R.icon className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="min-w-0"><p className="text-sm font-semibold">{R.label}{res.winner ? `: “${res.winner.name}”` : ""}</p><p className="text-xs opacity-90">{res.explanation}</p>
                {res.winner?.note && <p className="text-xs text-red-600 mt-1">{res.winner.note}</p>}
                {res.session && <p className="text-xs mt-1">Sessione attiva in “{res.session.automation}” (step {res.session.step_key}){res.session.option ? ` · opzione riconosciuta: ${res.session.option}` : ""}</p>}
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {res.automations.length === 0 && <p className="text-xs text-slate-500">Nessuna automazione attiva.</p>}
              {res.automations.map((a) => (
                <div key={a.id} className={`rounded-lg border p-2.5 ${a.matched ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`diagnose-auto-${a.id}`}>
                  <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p><span className="text-[10px] text-slate-500 shrink-0">priorità {a.priority} · {a.steps} step</span></div>
                  {a.triggers.map((t, i) => (
                    <p key={i} className="text-xs flex items-start gap-1.5 mt-1">
                      {t.matched ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />}
                      <span className={t.matched ? "text-emerald-800" : "text-slate-500"}><b>{TRIGGERS[t.type]?.label || t.type}</b>{t.value ? ` “${t.value}”` : ""} — {t.reason}</span>
                    </p>
                  ))}
                </div>
              ))}
            </div>
            {res.disabled_count > 0 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="diagnose-disabled-note">{res.disabled_count} automazion{res.disabled_count === 1 ? "e è spenta" : "i sono spente"} e non vengono valutate: accendi l'interruttore se devono rispondere.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DropAlertsDialog() {
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState(0);
  const [email, setEmail] = useState("");
  const [minVisits, setMinVisits] = useState(5);
  const [fallback, setFallback] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/app-settings").then((r) => { setThreshold(r.data.drop_alert_threshold || 0); setEmail(r.data.drop_alert_email || ""); setMinVisits(r.data.drop_alert_min_visits || 5); setFallback(r.data.reminder_email || ""); }).catch(() => {});
  }, [open]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/app-settings", { drop_alert_threshold: threshold, drop_alert_email: email, drop_alert_min_visits: minVisits }); toast.success(threshold > 0 ? `Avviso attivo: email se un nodo supera il ${threshold}% di abbandoni` : "Avviso abbandoni disattivato"); setOpen(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/automations/drop-alerts/run"); toast.success(data.skipped ? `Controllo saltato: ${data.reason}` : data.sent ? `Email inviata: ${data.sent} nodi sopra soglia` : "Nessun nodo sopra la soglia negli ultimi 7 giorni"); }
    catch { toast.error("Controllo non riuscito"); }
    finally { setBusy(false); }
  };
  const on = threshold > 0;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="drop-alerts-button" className="bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg">
          <BellRing className="h-4 w-4 mr-2" /> Avviso abbandoni
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="drop-alerts-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">Avviso abbandoni via email</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Ogni mattina controlliamo gli ultimi 7 giorni: se in un nodo la percentuale di clienti che si fermano senza rispondere supera la soglia, ricevi un'email (una sola volta al giorno per nodo).</p>
        <label className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm">
          <span className="font-medium text-slate-800">Avviso attivo</span>
          <Switch checked={on} onCheckedChange={(v) => setThreshold(v ? (threshold || 40) : 0)} data-testid="drop-alerts-switch" className="data-[state=checked]:bg-[#5BC65F]" />
        </label>
        {on && (
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-slate-600"><span>Soglia di abbandono</span><b className="text-slate-900" data-testid="drop-alerts-threshold-value">{threshold}%</b></div>
              <input type="range" min={5} max={95} step={5} value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value, 10))} data-testid="drop-alerts-threshold" className="w-full accent-[#5BC65F]" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-600">Minimo passaggi nel nodo per considerarlo (evita falsi allarmi)</label>
              <Input type="number" min={1} value={minVisits} onChange={(e) => setMinVisits(parseInt(e.target.value || "1", 10))} data-testid="drop-alerts-min-visits" className="bg-white border-slate-300 text-slate-900 w-28" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-600 flex items-center gap-1"><Mail className="h-3 w-3" /> Email di destinazione</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={fallback || "nome@azienda.it"} data-testid="drop-alerts-email" className="bg-white border-slate-300 text-slate-900" />
              {!email && fallback && <p className="text-[11px] text-slate-400">Vuoto = usa l'email dei promemoria ({fallback})</p>}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} data-testid="drop-alerts-save" className="flex-1 bg-[#5BC65F] hover:bg-[#45B049] text-white font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          {on && <Button variant="outline" onClick={runNow} disabled={busy} data-testid="drop-alerts-run-now" className="bg-white border-slate-300 text-slate-700">Controlla ora</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
